import { useState } from 'react';
import { formatFileSize } from '../lib/utils';
import { extractTextFromPDF } from '../lib/pdf';
import { MAX_FILE_SIZE, ALLOWED_FILE_TYPES } from '../constants/app';
import { analyzeFile } from '../api/analysis';
import { useProject, useUpdateProject } from './queries/useProjects';
import { logger } from '../utils/logger';

// Adjust retry parameters based on environment
const isTestEnv = import.meta.env.NODE_ENV === 'test' || import.meta.env.VITEST;
const MAX_RETRIES = 3;
const RETRY_DELAY = isTestEnv ? 10 : 1000; // 10ms in tests, 1 second in production

/**
 * Interface for useFileUpload hook return value
 */
export interface UseFileUploadReturn {
  /** Current file upload error message or null */
  uploadError: string | null;
  /** Function to set the upload error message */
  setUploadError: (error: string | null) => void;
  /** Whether a file is currently being processed */
  isProcessingFile: boolean;
  /** Function to handle file upload and processing */
  handleFileUpload: (file: File) => Promise<void>;
}

/**
 * Hook for handling file uploads, processing and analysis
 * @param projectId - The ID of the project to upload to
 * @returns Object containing upload state and handlers
 */
export function useFileUpload(projectId: string | null): UseFileUploadReturn {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState<boolean>(false);
  
  // React Query hooks
  const { data: activeProject } = useProject(projectId);
  const updateProjectMutation = useUpdateProject();

  const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

  const retryWithDelay = async <T>(
    fn: () => Promise<T>,
    retries = MAX_RETRIES,
    currentDelay = RETRY_DELAY
  ): Promise<T> => {
    try {
      return await fn();
    } catch (error) {
      if (retries === 0) throw error;
      await delay(currentDelay);
      const nextDelay = isTestEnv ? currentDelay : currentDelay * 2;
      return retryWithDelay(fn, retries - 1, nextDelay);
    }
  };

  const handleFileUpload = async (file: File): Promise<void> => {
    if (!projectId || !activeProject) {
      setUploadError('No active project selected');
      return;
    }

    setUploadError(null);

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setUploadError('Only PDF files are supported');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File size must be less than ${formatFileSize(MAX_FILE_SIZE)}`);
      return;
    }

    setIsProcessingFile(true);

    try {
      // Extract text from PDF
      const fileContent = await extractTextFromPDF(file);

      if (!fileContent.trim()) {
        throw new Error('Could not extract text from PDF. The file might be empty or corrupted.');
      }

      // Get file analysis with retry logic
      const analysis = await retryWithDelay(() => analyzeFile(fileContent));

      if (analysis) {
        // Update project context with retry logic using React Query mutation
        await retryWithDelay(async () => {
          await updateProjectMutation.mutateAsync({
            projectId,
            updates: {
              context: activeProject.context
                ? `${activeProject.context}\n\nFile Analysis: ${analysis}`
                : `File Analysis: ${analysis}`,
            }
          });
          
          logger.store('Updated project context with file analysis', {
            projectId,
            fileSize: file.size,
            fileType: file.type,
            analysisLength: analysis.length
          });
          
          return true;
        });
      } else {
        throw new Error('Failed to generate analysis for the file');
      }
    } catch (error) {
      console.error('Error processing file:', error);

      let errorMessage = 'Failed to process file. ';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorMessage += 'Please check your internet connection and try again.';
        } else {
          errorMessage += error.message;
        }
      } else {
        errorMessage += 'Please try again.';
      }

      setUploadError(errorMessage);
    } finally {
      setIsProcessingFile(false);
    }
  };

  return {
    uploadError,
    setUploadError,
    isProcessingFile,
    handleFileUpload,
  };
}